import React, { useCallback, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { media } from '../../../styles/brand';
import {
  CopyIcon,
  DownloadIcon,
  QrCodeIcon,
  TvIcon,
} from '../../../assets/svg/SVGComponents';
import { BoardQrSvg, boardUrl, useBoardQrDownloads } from '../../BoardQrCode';
import { useToast } from '../../../hooks/toast';
import BottomSheet from '../BottomSheet';
import {
  ActionButton,
  Actions,
  Card,
  Downloads,
  IconBox,
  LinkBox,
  LinkButton,
  QrFrame,
  SheetButton,
  SheetButtons,
  SheetQr,
  SheetText,
  Texts,
  Top,
} from './styles';

interface IBoardLinkCardProps {
  token: string;
  tournamentName: string;
  /** Força o layout do celular. Sem a prop, decide pela largura (< 48rem). */
  compact?: boolean;
}

const ICON_PROPS = {
  'aria-hidden': true,
  stroke: 'currentColor',
  fill: 'none',
  strokeWidth: 1.75,
} as const;

const SUBTITLE = 'Link fixo. Pode imprimir antes do evento.';

/**
 * Link e QR code do telão. Fica dentro do card principal do torneio (área
 * `board` do grid): coluna lateral no desktop, faixa larga entre 48 e 80rem e
 * faixa compacta no celular, com o QR num bottom sheet.
 */
const BoardLinkCard: React.FC<IBoardLinkCardProps> = ({
  token,
  tournamentName,
  compact,
}) => {
  const { addToast } = useToast();
  const isMobile = useMediaQuery({ query: media.md });
  const isCompact = compact ?? isMobile;
  const [sheetOpen, setSheetOpen] = useState(false);

  const url = boardUrl(token);
  const fileName = `telao-${tournamentName}`;

  const openBoard = useCallback(() => {
    window.open(url, '_blank', 'noopener');
  }, [url]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      addToast({
        type: 'success',
        expression: 'Link copiado!',
        description: 'Cole onde quiser compartilhar o telão.',
      });
    } catch {
      addToast({
        type: 'error',
        expression: 'Ops!',
        description:
          'Não foi possível copiar. Selecione o link e copie manualmente.',
      });
    }
  }, [url, addToast]);

  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const { svgRef, hiddenCanvas, downloadPng, downloadSvg } =
    useBoardQrDownloads(url, fileName);

  if (isCompact) {
    return (
      <Card aria-label="Telão do torneio">
        <Top>
          <IconBox>
            <TvIcon {...ICON_PROPS} />
          </IconBox>
          <Texts>
            <h3>Telão do torneio</h3>
            <p>{SUBTITLE}</p>
          </Texts>
        </Top>
        <Actions $compact>
          <ActionButton type="button" $primary onClick={openBoard}>
            <TvIcon {...ICON_PROPS} />
            Abrir telão
          </ActionButton>
          <ActionButton type="button" onClick={copyLink}>
            <CopyIcon {...ICON_PROPS} />
            Copiar link
          </ActionButton>
          <ActionButton
            type="button"
            aria-label="Ver QR code do telão"
            onClick={() => setSheetOpen(true)}
          >
            <QrCodeIcon {...ICON_PROPS} />
          </ActionButton>
        </Actions>
        {hiddenCanvas}

        <BottomSheet
          open={sheetOpen}
          onClose={closeSheet}
          title="QR code do telão"
        >
          <SheetQr>
            <BoardQrSvg ref={svgRef} url={url} size={200} />
          </SheetQr>
          <SheetText>
            Imprima e cole na entrada do evento. O link não muda depois que o
            torneio termina.
          </SheetText>
          <LinkBox>
            <span>{url}</span>
            <button type="button" aria-label="Copiar link" onClick={copyLink}>
              <CopyIcon {...ICON_PROPS} />
            </button>
          </LinkBox>
          <SheetButtons>
            <SheetButton type="button" onClick={downloadPng}>
              <DownloadIcon {...ICON_PROPS} />
              Baixar PNG
            </SheetButton>
            <SheetButton type="button" onClick={downloadSvg}>
              <DownloadIcon {...ICON_PROPS} />
              Baixar SVG
            </SheetButton>
            <SheetButton type="button" $primary onClick={openBoard}>
              <TvIcon {...ICON_PROPS} />
              Abrir telão
            </SheetButton>
          </SheetButtons>
        </BottomSheet>
      </Card>
    );
  }

  return (
    <Card aria-label="Telão do torneio">
      <Top>
        <QrFrame>
          <BoardQrSvg ref={svgRef} url={url} size={84} />
        </QrFrame>
        <Texts>
          <h3>
            <TvIcon {...ICON_PROPS} />
            Telão
          </h3>
          <p>{SUBTITLE}</p>
        </Texts>
      </Top>
      <Actions>
        <ActionButton type="button" $primary onClick={openBoard}>
          Abrir telão
        </ActionButton>
        <ActionButton type="button" onClick={copyLink}>
          <CopyIcon {...ICON_PROPS} />
          Copiar
        </ActionButton>
      </Actions>
      <Downloads>
        <LinkButton type="button" onClick={downloadPng}>
          <DownloadIcon {...ICON_PROPS} />
          PNG
        </LinkButton>
        <LinkButton type="button" onClick={downloadSvg}>
          <DownloadIcon {...ICON_PROPS} />
          SVG
        </LinkButton>
      </Downloads>
      {hiddenCanvas}
    </Card>
  );
};

export default BoardLinkCard;
